import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, X, ChevronRight, ChevronDown, SkipForward, FileText, ClipboardList } from 'lucide-react';
import Modal from '../common/modals/Modal';
import { useTheme } from '../../context/ThemeContext';
import { apiUpload, apiGet, apiPost } from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  final_approval: 'Approved', provisional_approval: 'Provisional',
  corrections_received: 'Corr. Received', corrections_requested: 'Corr. Requested',
  corrections_needed: 'Corr. Needed', in_review: 'In Review',
  file_received: 'Received', pending: 'Pending', canceled: 'Canceled',
};

const PROP_STATUS_LABELS = {
  review_complete: 'Review Complete', in_review: 'In Review', all_received: 'All Received',
};

const ReviewPipelineUploadModal = ({ isOpen, onClose, auditId, existingProperties = [], onImported }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | parsing | review | importing | done
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [importStats, setImportStats] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [editableItems, setEditableItems] = useState([]); // combined rent_roll + status_report items

  useEffect(() => {
    if (!isOpen) {
      setStep('upload'); setSelectedFiles([]); setParseResult(null);
      setImportStats(null); setEditableItems([]); setExpandedIdx(null);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [isOpen]);

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const addFiles = (newFiles) => {
    const arr = Array.from(newFiles);
    setSelectedFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...arr.filter((f) => !names.has(f.name))];
    });
  };

  const removeFile = (idx) => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));

  // ── Upload & Parse ──
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setStep('parsing');
    try {
      const formData = new FormData();
      selectedFiles.forEach((f) => formData.append('files[]', f));
      const response = await apiUpload(`/admin/audit_tracker/audits/${auditId}/import_status_reports`, formData);
      if (response.status === 202) {
        const data = await response.json();
        pollRef.current = setInterval(() => pollStatus(data.task_id), 2000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) { toast.error(err.message); setStep('upload'); }
  };

  const pollStatus = useCallback(async (taskId) => {
    try {
      const response = await apiGet(`/admin/audit_tracker/audits/${auditId}/import_status_reports_status/${taskId}`);
      if (!response.ok) return;
      const data = await response.json();
      if (data.status === 'done') {
        clearInterval(pollRef.current); pollRef.current = null;
        if (data.result?.success) {
          setParseResult(data.result);
          const items = (data.result.properties || []).map((p) => {
            const cleanId = p.clean_id || '';
            const match = existingProperties.find((ep) => ep.clean_id === cleanId || ep.property_name?.toUpperCase() === cleanId);
            return {
              ...p,
              _matched: !!match, _matchedPropId: match?.id || null,
              _skip: false,
              _editedName: p.property_name || '',
              _editedState: p.detected_state || '',
            };
          });
          setEditableItems(items);
          setStep('review');
        } else { toast.error(data.result?.error || 'Parse failed'); setStep('upload'); }
      }
    } catch { /* keep polling */ }
  }, [auditId, existingProperties]);

  // ── Confirm ──
  const handleConfirm = async () => {
    const active = editableItems.filter((p) => !p._skip);
    if (active.length === 0) { toast.error('No properties selected'); return; }
    setStep('importing');

    const statusReports = active.filter((p) => p.file_type === 'status_report');
    const rentRolls = active.filter((p) => p.file_type === 'rent_roll');
    const allStats = { properties_created: 0, properties_updated: 0, files_created: 0, files_updated: 0, corrections_created: 0, rent_roll_properties: 0, total_units: 0, errors: [] };

    try {
      // Import status reports
      if (statusReports.length > 0) {
        const payload = statusReports.map((p) => ({
          property_name: p._editedName || p.property_name,
          clean_id: p.clean_id, detected_state: p._editedState || p.detected_state,
          suggested_status: p.suggested_status, tenant_files: p.tenant_files,
        }));
        const res = await apiPost(`/admin/audit_tracker/audits/${auditId}/import_status_reports_confirm`, { properties: payload });
        if (res.ok) {
          const data = await res.json();
          Object.entries(data.stats || {}).forEach(([k, v]) => { if (typeof v === 'number') allStats[k] = (allStats[k] || 0) + v; });
          if (data.stats?.errors) allStats.errors.push(...data.stats.errors);
        } else {
          const data = await res.json();
          allStats.errors.push(data.error || 'Status report import failed');
        }
      }

      // Import rent rolls (create properties)
      if (rentRolls.length > 0) {
        const payload = rentRolls.map((p) => ({
          property_name: p._editedName || p.property_name,
          clean_id: p.clean_id, detected_state: p._editedState || p.detected_state,
          total_units: p.total_units, occupied: p.occupied,
        }));
        const res = await apiPost(`/admin/audit_tracker/audits/${auditId}/bulk_rent_roll_confirm`, { rent_rolls: payload });
        if (res.ok) {
          const data = await res.json();
          allStats.rent_roll_properties = (data.stats?.properties_created || 0) + (data.stats?.properties_updated || 0);
          allStats.properties_created += data.stats?.properties_created || 0;
          allStats.properties_updated += data.stats?.properties_updated || 0;
          allStats.total_units += data.stats?.total_units || 0;
          if (data.stats?.errors) allStats.errors.push(...data.stats.errors);
        } else {
          const data = await res.json();
          allStats.errors.push(data.error || 'Rent roll import failed');
        }
      }

      setImportStats(allStats);
      setStep('done');
      toast.success('Import complete');
      if (onImported) onImported();
    } catch (err) { toast.error(err.message); setStep('review'); }
  };

  const toggleSkip = (idx) => setEditableItems((prev) => prev.map((p, i) => i === idx ? { ...p, _skip: !p._skip } : p));
  const updateField = (idx, field, value) => setEditableItems((prev) => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));

  const activeItems = editableItems.filter((p) => !p._skip);
  const statusReportCount = activeItems.filter((p) => p.file_type === 'status_report').length;
  const rentRollCount = activeItems.filter((p) => p.file_type === 'rent_roll').length;
  const skippedCount = editableItems.filter((p) => p._skip).length;
  const failedCount = parseResult?.errors?.length || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 'parsing' || step === 'importing' ? undefined : onClose}
      title="Upload Files"
      size="3xl"
      footer={
        step === 'upload' ? (
          <>
            <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Cancel</button>
            <button onClick={handleUpload} disabled={selectedFiles.length === 0} className="px-4 py-2 rounded-lg text-sm font-rasa bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              Upload & Parse ({selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''})
            </button>
          </>
        ) : step === 'review' ? (
          <>
            <button onClick={() => { setStep('upload'); setParseResult(null); setEditableItems([]); }} className={`px-4 py-2 rounded-lg text-sm font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Back</button>
            <button onClick={handleConfirm} disabled={activeItems.length === 0} className="px-4 py-2 rounded-lg text-sm font-rasa bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              Confirm ({activeItems.length} properties)
            </button>
          </>
        ) : step === 'done' ? (
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-rasa bg-blue-600 hover:bg-blue-700 text-white">Done</button>
        ) : null
      }
    >
      <div className="min-h-[250px]">
        {/* ── Upload ── */}
        {step === 'upload' && (
          <div className="space-y-4">
            <p className={`text-sm font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              Upload rent rolls or status reports (.xls, .xlsx, .csv). AI will detect the file type and extract data. Multiple files supported.
            </p>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
              className={`flex items-center justify-center p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${dragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : d ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'}`}
            >
              <div className="text-center">
                <Upload size={28} className={`mx-auto mb-2 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`text-sm font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{dragging ? 'Drop files here' : 'Drag & drop rent rolls or status reports'}</span>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.csv" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }} />
            {selectedFiles.length > 0 && (
              <div className="space-y-1">
                <div className={`text-xs font-rasa font-bold uppercase tracking-wider ${d ? 'text-gray-400' : 'text-gray-500'}`}>{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</div>
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {selectedFiles.map((f, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded-lg ${d ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet size={14} className="text-green-500" />
                        <span className={`text-sm font-rasa truncate max-w-[300px] ${d ? 'text-gray-200' : 'text-gray-700'}`}>{f.name}</span>
                        <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>({(f.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <button onClick={() => removeFile(i)} className="p-1 text-red-400 hover:text-red-500"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Parsing ── */}
        {step === 'parsing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
            <p className={`text-sm font-rasa ${d ? 'text-gray-300' : 'text-gray-600'}`}>Parsing {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}...</p>
            <p className={`text-xs font-rasa mt-1 ${d ? 'text-gray-500' : 'text-gray-400'}`}>Detecting file types, mapping columns, extracting data</p>
          </div>
        )}

        {/* ── Review ── */}
        {step === 'review' && (
          <div className="space-y-4">
            {/* Summary badges */}
            <div className="flex items-center gap-3 flex-wrap">
              {rentRollCount > 0 && (
                <span className={`flex items-center gap-1 text-xs font-rasa font-bold px-2.5 py-1 rounded-full ${d ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                  <ClipboardList size={11} /> {rentRollCount} rent roll{rentRollCount !== 1 ? 's' : ''}
                </span>
              )}
              {statusReportCount > 0 && (
                <span className={`flex items-center gap-1 text-xs font-rasa font-bold px-2.5 py-1 rounded-full ${d ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
                  <FileText size={11} /> {statusReportCount} status report{statusReportCount !== 1 ? 's' : ''}
                </span>
              )}
              {skippedCount > 0 && (
                <span className={`text-xs font-rasa font-bold px-2.5 py-1 rounded-full ${d ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>{skippedCount} skipped</span>
              )}
              {failedCount > 0 && (
                <span className={`text-xs font-rasa font-bold px-2.5 py-1 rounded-full ${d ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>{failedCount} failed</span>
              )}
            </div>

            {/* Rent roll info banner */}
            {rentRollCount > 0 && (
              <div className={`p-2.5 rounded-lg text-xs font-rasa ${d ? 'bg-indigo-900/15 border border-indigo-800/30 text-indigo-300' : 'bg-indigo-50 border border-indigo-200 text-indigo-700'}`}>
                Rent rolls will create properties and populate unit counts. Run <strong>File Selection</strong> on each property afterwards to pick tenants for audit.
              </div>
            )}

            {/* Parse errors */}
            {parseResult?.errors?.length > 0 && (
              <div className={`p-3 rounded-lg text-xs font-rasa ${d ? 'bg-red-900/20 border border-red-800 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                <div className="flex items-center gap-1.5 mb-1 font-bold"><AlertTriangle size={12} /> Failed to parse:</div>
                {parseResult.errors.map((e, i) => <div key={i}>{e.filename}: {e.error}</div>)}
              </div>
            )}

            {/* Property list */}
            <div className="max-h-[400px] overflow-y-auto space-y-1.5">
              {editableItems.map((item, idx) => {
                const isExpanded = expandedIdx === idx;
                const isRentRoll = item.file_type === 'rent_roll';
                const typeBadge = isRentRoll
                  ? { cls: d ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-700', label: 'Rent Roll' }
                  : { cls: d ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-100 text-violet-700', label: 'Status Report' };
                const matchBadge = item._matched
                  ? { cls: d ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700', label: 'Matched' }
                  : { cls: d ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700', label: 'New' };

                return (
                  <div key={idx} className={`rounded-lg border ${item._skip ? 'opacity-50' : ''} ${d ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
                    {/* Header */}
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button onClick={() => setExpandedIdx(isExpanded ? null : idx)} className={`p-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                      <span className={`text-[10px] font-rasa font-bold px-1.5 py-0.5 rounded ${typeBadge.cls}`}>{typeBadge.label}</span>
                      <span className={`text-[10px] font-rasa font-bold px-1.5 py-0.5 rounded ${matchBadge.cls}`}>{matchBadge.label}</span>
                      {item.duplicate_of && <span className={`text-[10px] font-rasa px-1.5 py-0.5 rounded ${d ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>Dup</span>}

                      <span className={`flex-1 text-sm font-rasa font-medium truncate ${d ? 'text-gray-200' : 'text-gray-700'}`}>{item._editedName || item.property_name}</span>

                      <span className={`text-xs font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                        {isRentRoll ? `${item.total_units || 0} units` : `${item.tenant_files_count || 0} files`}
                      </span>

                      {!isRentRoll && item.suggested_status && (
                        <span className={`text-[10px] font-rasa px-1.5 py-0.5 rounded ${d ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
                          → {PROP_STATUS_LABELS[item.suggested_status] || item.suggested_status}
                        </span>
                      )}

                      <span className={`text-[10px] font-rasa ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                        {item.detection_method === 'zeffert' ? 'Zeffert' : 'AI'}
                      </span>

                      <button onClick={() => toggleSkip(idx)} className={`p-1 rounded ${item._skip ? 'text-blue-500' : (d ? 'text-gray-500' : 'text-gray-400')}`} title={item._skip ? 'Include' : 'Skip'}>
                        <SkipForward size={13} />
                      </button>
                    </div>

                    {/* Expanded */}
                    {isExpanded && (
                      <div className={`px-4 pb-3 border-t ${d ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div className="grid grid-cols-3 gap-3 py-3">
                          <div>
                            <label className={`text-[10px] font-rasa font-bold uppercase ${d ? 'text-gray-500' : 'text-gray-400'}`}>Property Name</label>
                            <input value={item._editedName} onChange={(e) => updateField(idx, '_editedName', e.target.value)} className={`w-full mt-0.5 px-2 py-1 text-xs font-rasa rounded border ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`} />
                          </div>
                          <div>
                            <label className={`text-[10px] font-rasa font-bold uppercase ${d ? 'text-gray-500' : 'text-gray-400'}`}>State</label>
                            <input value={item._editedState} onChange={(e) => updateField(idx, '_editedState', e.target.value.toUpperCase().slice(0, 2))} placeholder="e.g. GA" className={`w-full mt-0.5 px-2 py-1 text-xs font-rasa rounded border ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`} />
                          </div>
                          <div>
                            <label className={`text-[10px] font-rasa font-bold uppercase ${d ? 'text-gray-500' : 'text-gray-400'}`}>Info</label>
                            <div className={`mt-0.5 px-2 py-1 text-xs font-rasa rounded ${d ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                              {item.detection_method === 'zeffert' ? 'Zeffert format' : 'AI mapped'}
                              {item.analyst && ` · ${item.analyst}`}
                              {item.pn_number && ` · PN ${item.pn_number}`}
                              {isRentRoll && ` · ${item.occupied || 0} occupied, ${item.vacant || 0} vacant`}
                            </div>
                          </div>
                        </div>

                        {/* Data table — different for each type */}
                        <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
                          {isRentRoll ? (
                            <table className="w-full text-[11px] font-rasa">
                              <thead className="sticky top-0">
                                <tr className={`border-b ${d ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                                  {['Tenant', 'Unit', 'Building', 'Occupancy', 'Move-In', 'Designation'].map((h) => (
                                    <th key={h} className={`px-2 py-1 text-left font-bold uppercase ${d ? 'text-gray-500' : 'text-gray-400'}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(item.tenants || []).slice(0, 50).map((t, ti) => (
                                  <tr key={ti} className={`border-b ${d ? 'border-gray-700/30' : 'border-gray-100'} ${t.is_vacant ? 'opacity-40' : ''}`}>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-300' : 'text-gray-700'}`}>{t.tenant_name}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{t.unit || '-'}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{t.building || '-'}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{t.occupancy_status || (t.is_vacant ? 'Vacant' : 'Occupied')}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{t.move_in_date || '-'}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{t.current_designation || '-'}</td>
                                  </tr>
                                ))}
                                {(item.tenants || []).length > 50 && (
                                  <tr><td colSpan={6} className={`px-2 py-2 text-center ${d ? 'text-gray-500' : 'text-gray-400'}`}>...and {item.tenants.length - 50} more</td></tr>
                                )}
                              </tbody>
                            </table>
                          ) : (
                            <table className="w-full text-[11px] font-rasa">
                              <thead className="sticky top-0">
                                <tr className={`border-b ${d ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                                  {['Tenant', 'Unit', 'Building', 'Status', 'Received', 'Corrections', 'Final'].map((h) => (
                                    <th key={h} className={`px-2 py-1 text-left font-bold uppercase ${d ? 'text-gray-500' : 'text-gray-400'}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(item.tenant_files || []).map((tf, tfi) => (
                                  <tr key={tfi} className={`border-b ${d ? 'border-gray-700/30' : 'border-gray-100'} ${tf.status === 'canceled' ? 'opacity-40' : ''}`}>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-300' : 'text-gray-700'}`}>{tf.tenant_name}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.unit || '-'}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.building || '-'}</td>
                                    <td className="px-2 py-1">
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                        tf.status === 'final_approval' ? (d ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700') :
                                        tf.status === 'canceled' ? (d ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-500') :
                                        tf.status?.includes('corrections') ? (d ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700') :
                                        (d ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                                      }`}>{STATUS_LABELS[tf.status] || tf.status}</span>
                                    </td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.file_received_date || '-'}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.corrections?.length || 0}</td>
                                    <td className={`px-2 py-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.final_approval_date || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Importing ── */}
        {step === 'importing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
            <p className={`text-sm font-rasa ${d ? 'text-gray-300' : 'text-gray-600'}`}>Importing {activeItems.length} properties...</p>
          </div>
        )}

        {/* ── Done ── */}
        {step === 'done' && importStats && (
          <div className="space-y-3 py-4">
            <div className={`flex items-center gap-2 p-3 rounded-lg ${d ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <CheckCircle2 size={18} className="text-green-500" />
              <span className={`text-sm font-rasa ${d ? 'text-green-300' : 'text-green-700'}`}>Import complete</span>
            </div>
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-lg ${d ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="text-center">
                <div className="text-xl font-dm-serif text-green-500">{importStats.properties_created || 0}</div>
                <div className={`text-xs font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Properties Created</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-dm-serif text-blue-500">{importStats.properties_updated || 0}</div>
                <div className={`text-xs font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Properties Updated</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-dm-serif text-violet-500">{importStats.files_created || 0}</div>
                <div className={`text-xs font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Tenant Files</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-dm-serif text-indigo-500">{importStats.total_units || 0}</div>
                <div className={`text-xs font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Units (Rent Rolls)</div>
              </div>
            </div>
            {importStats.rent_roll_properties > 0 && (
              <p className={`text-xs font-rasa text-center ${d ? 'text-indigo-400' : 'text-indigo-600'}`}>
                {importStats.rent_roll_properties} rent roll properties created — run File Selection on each to pick tenants
              </p>
            )}
            {importStats.errors?.length > 0 && (
              <div className={`p-3 rounded-lg ${d ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-1"><AlertTriangle size={12} className="text-red-500" /><span className={`text-xs font-rasa font-bold ${d ? 'text-red-300' : 'text-red-700'}`}>{importStats.errors.length} errors</span></div>
                {importStats.errors.slice(0, 5).map((err, i) => <div key={i} className={`text-xs font-rasa ${d ? 'text-red-400' : 'text-red-600'}`}>{err}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReviewPipelineUploadModal;
